/* eslint-disable max-lines */
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm
} from "react-hook-form";
import toast from "react-hot-toast";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import FormWrapper from "../../components/ui/FormWrapper";
import Input from "../../components/ui/input/Input";
import Select from "../../components/ui/select/Select";
import useFetch from "../../hooks/useFetch";
import {
  ICreateCustomReservationThreshold,
  ICustomReservationThreshold
} from "../../interfaces/endpoints/ICustomReservationThreshold";
import {
  ICity,
  ICountry,
  ILocation
} from "../../interfaces/endpoints/ILocations";
import { OperatorsSelectList } from "../../interfaces/endpoints/IOperators";
import {
  createThreshold,
  getThresholdById,
  updateThreshold
} from "../../services/CustomReservationThreshold.service";
import {
  getAllCities,
  getAllCountries,
  getAllLocations,
  getCurrentLocation,
  getCurrentStation
} from "../../services/Locations.service";
import {
  getAllSelectOperators,
  getCurrentOperators
} from "../../services/Operators.service";
import CustomReservationThresholdSchema from "../../utils/validations/CustomReservationThreshold.schema";

// How wide the rule is. The backend matches the narrowest first: the station pair, then the city pair,
// then the country pair — "anything from Kosovo to Germany", or the same country twice for every
// journey inside it.
type ThresholdMode = "station" | "city" | "country";

// The countries endpoint returns them newest first, which puts Zimbabwe at the top of the picker.
// Albanian collation so ç and ë sort where a reader expects them.
const byName = (list?: ICountry[]): ICountry[] =>
  [...(list ?? [])].sort((a, b) =>
    (a.countryName ?? "").localeCompare(b.countryName ?? "", "sq")
  );

interface ThresholdFormData {
  fromStation: ILocation | null;
  toStation: ILocation | null;
  operator: OperatorsSelectList | null;
  fromCity: ICity | null;
  toCity: ICity | null;
  fromCountry: ICountry | null;
  toCountry: ICountry | null;
  threshold: number;
}

const AddThreshold: React.FC<{ editMode?: boolean }> = ({
  editMode
}) => {
  const { id } = useParams();
  const { state } = useLocation() as {
    state: {
      page?: number;
      thresholdData?: ICustomReservationThreshold;
    } | null;
  };
  const navigate = useNavigate();

  const [type, setType] = useState<ThresholdMode>("station");
  const [stations, setStations] = useState<ILocation[]>([]);
  const [cities, setCities] = useState<ICity[]>([]);
  const [countries, setCountries] = useState<ICountry[]>([]);

  useEffect(() => {
    let alive = true;
    void getAllLocations({ pageIndex: 1, pageSize: 100000 })
      .then((res) => {
        if (alive) setStations(res.data?.result ?? []);
      })
      .catch(() => {
        /* leave empty; user can still type to search */
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (type !== "city" || cities.length) return;
    let alive = true;
    void getAllCities({ pageIndex: 1, pageSize: 100000 })
      .then((res) => {
        if (alive) setCities(res.data?.result ?? []);
      })
      .catch(() => {
        /* leave empty */
      });
    return () => {
      alive = false;
    };
  }, [type, cities.length]);

  // Countries are a short list, but it is still only fetched once the type asks for it, or when an
  // existing country threshold is being edited and its names have to be shown.
  useEffect(() => {
    if (type !== "country" || countries.length) return;
    let alive = true;
    void getAllCountries({ pageIndex: 1, pageSize: 1000 })
      .then((res) => {
        if (alive) setCountries(byName(res.data?.result));
      })
      .catch(() => {
        /* leave empty */
      });
    return () => {
      alive = false;
    };
  }, [type, countries.length]);

  const methods = useForm<ThresholdFormData>({
    resolver: yupResolver(CustomReservationThresholdSchema),
    context: { type },
    defaultValues: {
      fromStation: null,
      toStation: null,
      operator: null,
      fromCity: null,
      toCity: null,
      fromCountry: null,
      toCountry: null,
      threshold: undefined
    }
  });

  const {
    handleSubmit,
    register,
    setValue,
    control,
    formState: { errors }
  } = methods;

  const { response } = useFetch<
    ICustomReservationThreshold,
    number
  >(getThresholdById, {
    initRequest: !!editMode && !!id,
    params: Number(id)
  });

  useEffect(() => {
    if (!editMode || !response?.data) return;
    const data = response.data;

    setValue("threshold", data.threshold);

    const isCityMode = data.fromCityId !== null;
    const isCountryMode = data.fromCountryId !== null;
    setType(isCountryMode ? "country" : isCityMode ? "city" : "station");

    const resolveStations = async (
      fromId: number,
      toId: number
    ) => {
      try {
        const [fromRes, toRes] = await Promise.all([
          getCurrentStation(fromId),
          getCurrentStation(toId)
        ]);
        setValue("fromStation", {
          id: fromId,
          name: fromRes.data?.stationName || `#${fromId}`
        });
        setValue("toStation", {
          id: toId,
          name: toRes.data?.stationName || `#${toId}`
        });
      } catch {
        setValue("fromStation", { id: fromId, name: `#${fromId}` });
        setValue("toStation", { id: toId, name: `#${toId}` });
      }
    };

    const resolveCities = async (fromId: number, toId: number) => {
      try {
        const [fromRes, toRes] = await Promise.all([
          getCurrentLocation(fromId),
          getCurrentLocation(toId)
        ]);
        setValue("fromCity", {
          id: fromId,
          locationName: fromRes.data?.locationName || `#${fromId}`
        });
        setValue("toCity", {
          id: toId,
          locationName: toRes.data?.locationName || `#${toId}`
        });
      } catch {
        setValue("fromCity", { id: fromId, locationName: `#${fromId}` });
        setValue("toCity", { id: toId, locationName: `#${toId}` });
      }
    };

    // Countries come from the one list the select uses; the fallback keeps the field filled even when
    // that list has not arrived, so saving does not silently drop the pair.
    const resolveCountries = async (fromId: number, toId: number) => {
      try {
        const res = await getAllCountries({
          pageIndex: 1,
          pageSize: 1000
        });
        const all = byName(res.data?.result);
        setCountries(all);
        setValue(
          "fromCountry",
          all.find((c) => c.id === fromId) ?? {
            id: fromId,
            countryName: `#${fromId}`,
            locales: []
          }
        );
        setValue(
          "toCountry",
          all.find((c) => c.id === toId) ?? {
            id: toId,
            countryName: `#${toId}`,
            locales: []
          }
        );
      } catch {
        setValue("fromCountry", {
          id: fromId,
          countryName: `#${fromId}`,
          locales: []
        });
        setValue("toCountry", {
          id: toId,
          countryName: `#${toId}`,
          locales: []
        });
      }
    };

    const resolveOperator = async (operatorId: number) => {
      try {
        const opRes = await getCurrentOperators(operatorId);
        setValue("operator", {
          id: operatorId,
          operatorName: opRes.data?.operatorName || `#${operatorId}`
        });
      } catch {
        setValue("operator", {
          id: operatorId,
          operatorName: `#${operatorId}`
        });
      }
    };

    if (
      isCountryMode &&
      data.fromCountryId !== null &&
      data.toCountryId !== null
    ) {
      void resolveCountries(data.fromCountryId, data.toCountryId);
    } else if (isCityMode && data.fromCityId !== null && data.toCityId !== null) {
      void resolveCities(data.fromCityId, data.toCityId);
    } else if (
      data.fromStationId !== null &&
      data.toStationId !== null
    ) {
      void resolveStations(data.fromStationId, data.toStationId);
      if (data.operatorId !== null) {
        void resolveOperator(data.operatorId);
      }
    }
  }, [editMode, response, setValue]);

  const onSubmit: SubmitHandler<ThresholdFormData> = async (
    data
  ) => {
    // Exactly one pair goes to the API; the other two are sent as null so an edit that switches type
    // clears what was there before.
    const payload: ICreateCustomReservationThreshold =
      type === "country"
        ? {
            fromCountryId: data.fromCountry!.id,
            toCountryId: data.toCountry!.id,
            fromCityId: null,
            toCityId: null,
            fromStationId: null,
            toStationId: null,
            operatorId: null,
            threshold: Number(data.threshold)
          }
        : type === "city"
        ? {
            fromCityId: data.fromCity!.id,
            toCityId: data.toCity!.id,
            fromStationId: null,
            toStationId: null,
            fromCountryId: null,
            toCountryId: null,
            operatorId: null,
            threshold: Number(data.threshold)
          }
        : {
            fromStationId: data.fromStation!.id,
            toStationId: data.toStation!.id,
            fromCityId: null,
            toCityId: null,
            fromCountryId: null,
            toCountryId: null,
            operatorId: data.operator ? data.operator.id : null,
            threshold: Number(data.threshold)
          };

    if (editMode && id) {
      await updateThreshold(Number(id), payload)
        .then(() => {
          toast.success("Pragu u ndryshua me sukses.", {
            position: "top-right"
          });
          setTimeout(() => {
            navigate(
              `/reservation-thresholds?pageindex=${
                state?.page ? state.page : 1
              }`,
              { state: ["Reservation Thresholds"] }
            );
          }, 500);
        })
        .catch((err) => {
          const msg =
            err?.response?.status === 409
              ? "Ky kombinim ekziston tashme."
              : "Ndodhi nje gabim gjate ndryshimit.";
          toast.error(msg, { position: "top-right" });
        });
    } else {
      await createThreshold(payload)
        .then(() => {
          toast.success("Pragu u shtua me sukses.", {
            position: "top-right"
          });
          setTimeout(() => {
            navigate("/reservation-thresholds", {
              state: ["Reservation Thresholds"]
            });
          }, 500);
        })
        .catch((err) => {
          const msg =
            err?.response?.status === 409
              ? "Ky kombinim ekziston tashme."
              : "Ndodhi nje gabim gjate krijimit.";
          toast.error(msg, { position: "top-right" });
        });
    }
  };

  const handleTypeChange = (next: ThresholdMode) => {
    setType(next);
    // Clear what the new type does not use, so a half-filled pair cannot travel with the save.
    if (next !== "station") {
      setValue("fromStation", null);
      setValue("toStation", null);
      setValue("operator", null);
    }
    if (next !== "city") {
      setValue("fromCity", null);
      setValue("toCity", null);
    }
    if (next !== "country") {
      setValue("fromCountry", null);
      setValue("toCountry", null);
    }
  };

  return (
    <FormProvider {...methods}>
      <Link
        className="flex items-center mb-4 text-sm font-semibold text-primary"
        to="/reservation-thresholds"
        state={["Reservation Thresholds"]}
      >
        <ChevronLeftIcon className="w-4 h-4 mr-2" />
        <span>Kthehu tek lista</span>
      </Link>
      <FormWrapper
        history={{ entityName: "CustomReservationThreshold", entityId: id }}
        showPrompt={
          !methods.formState.isSubmitSuccessful &&
          !!Object.keys(methods.formState.dirtyFields).length
        }
        onSubmit={handleSubmit(onSubmit)}
        title={
          editMode ? "Modifiko pragun" : "Shto prag te ri"
        }
      >
        <div className="mb-6">
          <label className="block mb-2 text-sm font-medium">
            Tipi
          </label>
          <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
            <button
              type="button"
              onClick={() => handleTypeChange("station")}
              className={`px-4 py-2 text-sm ${
                type === "station"
                  ? "bg-primary text-white"
                  : "bg-white text-gray-700"
              }`}
            >
              Stacion - Stacion
            </button>
            <button
              type="button"
              onClick={() => handleTypeChange("city")}
              className={`px-4 py-2 text-sm ${
                type === "city"
                  ? "bg-primary text-white"
                  : "bg-white text-gray-700"
              }`}
            >
              Qytet - Qytet
            </button>
            <button
              type="button"
              onClick={() => handleTypeChange("country")}
              className={`px-4 py-2 text-sm ${
                type === "country"
                  ? "bg-primary text-white"
                  : "bg-white text-gray-700"
              }`}
            >
              Shtet - Shtet
            </button>
          </div>
        </div>

        {type === "station" ? (
          <>
            <Controller
              control={control}
              name="fromStation"
              render={({
                field: { value, onChange },
                fieldState: { error }
              }) => (
                <Select
                  key={`stations-${stations.length}`}
                  options={stations}
                  searchFunc={(o, search) =>
                    !search ||
                    (o.name ?? "")
                      .toLocaleLowerCase()
                      .includes(search.toLocaleLowerCase())
                  }
                  onSelectedItemChange={onChange}
                  selectedItem={value}
                  label="Stacioni i nisjes"
                  className="mb-6"
                  placeholder="Kerko stacionin..."
                  getOptionLabel={(o) => o.name}
                  getOptionValue={(o) => o?.id}
                  itemToString={(o) => o?.name || ""}
                  error={error?.message}
                />
              )}
            />

            <Controller
              control={control}
              name="toStation"
              render={({
                field: { value, onChange },
                fieldState: { error }
              }) => (
                <Select
                  key={`stations-${stations.length}`}
                  options={stations}
                  searchFunc={(o, search) =>
                    !search ||
                    (o.name ?? "")
                      .toLocaleLowerCase()
                      .includes(search.toLocaleLowerCase())
                  }
                  onSelectedItemChange={onChange}
                  selectedItem={value}
                  label="Stacioni i arritjes"
                  className="mb-6"
                  placeholder="Kerko stacionin..."
                  getOptionLabel={(o) => o.name}
                  getOptionValue={(o) => o?.id}
                  itemToString={(o) => o?.name || ""}
                  error={error?.message}
                />
              )}
            />

            <Controller
              control={control}
              name="operator"
              render={({
                field: { value, onChange }
              }) => (
                <Select
                  asyncFunc={getAllSelectOperators}
                  onSelectedItemChange={onChange}
                  selectedItem={value}
                  label="Operatori (opsional — boshe = te gjithe)"
                  className="mb-6"
                  placeholder="Kerko operatorin..."
                  getOptionLabel={(o) =>
                    o.operatorName || `#${o.id}`
                  }
                  itemToString={(o) => o?.operatorName || ""}
                />
              )}
            />
          </>
        ) : type === "country" ? (
          <>
            <Controller
              control={control}
              name="fromCountry"
              render={({
                field: { value, onChange },
                fieldState: { error }
              }) => (
                <Select
                  key={`countries-${countries.length}`}
                  options={countries}
                  searchFunc={(o, search) =>
                    !search ||
                    (o.countryName ?? "")
                      .toLocaleLowerCase()
                      .includes(search.toLocaleLowerCase())
                  }
                  onSelectedItemChange={onChange}
                  selectedItem={value}
                  label="Shteti i nisjes"
                  className="mb-6"
                  // Shorter than the default max-h-80: the open list would otherwise run past the card
                  // and make the whole page scroll.
                  maxHeightList="max-h-60"
                  placeholder="Kerko shtetin..."
                  getOptionLabel={(o) => o.countryName}
                  getOptionValue={(o) => o?.id}
                  itemToString={(o) => o?.countryName || ""}
                  error={error?.message}
                />
              )}
            />

            <Controller
              control={control}
              name="toCountry"
              render={({
                field: { value, onChange },
                fieldState: { error }
              }) => (
                <Select
                  key={`countries-${countries.length}`}
                  options={countries}
                  searchFunc={(o, search) =>
                    !search ||
                    (o.countryName ?? "")
                      .toLocaleLowerCase()
                      .includes(search.toLocaleLowerCase())
                  }
                  onSelectedItemChange={onChange}
                  selectedItem={value}
                  label="Shteti i arritjes"
                  className="mb-6"
                  maxHeightList="max-h-60"
                  placeholder="Kerko shtetin..."
                  getOptionLabel={(o) => o.countryName}
                  getOptionValue={(o) => o?.id}
                  itemToString={(o) => o?.countryName || ""}
                  error={error?.message}
                />
              )}
            />
          </>
        ) : (
          <>
            <Controller
              control={control}
              name="fromCity"
              render={({
                field: { value, onChange },
                fieldState: { error }
              }) => (
                <Select
                  key={`cities-${cities.length}`}
                  options={cities}
                  searchFunc={(o, search) =>
                    !search ||
                    (o.locationName ?? "")
                      .toLocaleLowerCase()
                      .includes(search.toLocaleLowerCase())
                  }
                  onSelectedItemChange={onChange}
                  selectedItem={value}
                  label="Qyteti i nisjes"
                  className="mb-6"
                  placeholder="Kerko qytetin..."
                  getOptionLabel={(o) => o.locationName}
                  getOptionValue={(o) => o?.id}
                  itemToString={(o) => o?.locationName || ""}
                  error={error?.message}
                />
              )}
            />

            <Controller
              control={control}
              name="toCity"
              render={({
                field: { value, onChange },
                fieldState: { error }
              }) => (
                <Select
                  key={`cities-${cities.length}`}
                  options={cities}
                  searchFunc={(o, search) =>
                    !search ||
                    (o.locationName ?? "")
                      .toLocaleLowerCase()
                      .includes(search.toLocaleLowerCase())
                  }
                  onSelectedItemChange={onChange}
                  selectedItem={value}
                  label="Qyteti i arritjes"
                  className="mb-6"
                  placeholder="Kerko qytetin..."
                  getOptionLabel={(o) => o.locationName}
                  getOptionValue={(o) => o?.id}
                  itemToString={(o) => o?.locationName || ""}
                  error={error?.message}
                />
              )}
            />
          </>
        )}

        <Input
          {...register("threshold")}
          label="Pragu (minuta para nisjes)"
          className="mb-6"
          type="number"
          placeholder="p.sh. 120"
          error={errors.threshold?.message}
        />
      </FormWrapper>
    </FormProvider>
  );
};

export default AddThreshold;
